import { AuthContext } from '@core/shared';
import { EmailListStatus, OptionsApi } from '@core/swagger-client';
import { ContainerDefault, ErrorMessagePage, Heading, LoaderPage } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackLink, Link } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

const formatDate = (value?: string) =>
    value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : '-';

export const ManageEmailListsPage = () => {
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const { t } = useTranslation();
    // Rendered under both /manage/email-lists and /$webinarId/marketing — webinarId is only set in the latter.
    const { webinarId } = useParams({ strict: false });
    const { data: emailLists, isLoading, error } = useQuery(OptionsApi.emailLists.get({ refetchInterval: 15000 }));

    if (!canManageEmailLists) {
        return <ErrorMessagePage statusCode={404} />;
    }

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading || !emailLists) {
        return <LoaderPage />;
    }

    return (
        <ContainerDefault>
            {webinarId ? (
                <BackLink to="/$webinarId/content" params={{ webinarId }}>
                    {t('Button.Back')}
                </BackLink>
            ) : (
                <BackLink to="/manage">{t('Button.BackToManageEnv')}</BackLink>
            )}
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {webinarId ? t('Marketing.PageHeading') : t('EmailLists.PageHeading')}
            </Heading>
            {webinarId ? (
                <Link data-testid="email-list-import-link" to="/$webinarId/marketing/import" params={{ webinarId }} variant="primary">
                    {t('EmailLists.Button.Import')}
                </Link>
            ) : (
                <Link data-testid="email-list-import-link" to="/manage/email-lists/import" variant="primary">
                    {t('EmailLists.Button.Import')}
                </Link>
            )}

            {emailLists.length > 0 && (
                <>
                    <Heading variant="h2" classNames="mt-10 2xl:mt-12 mb-4 lg:mb-6 2xl:mb-8">
                        {t('EmailLists.Heading.YourLists')}
                    </Heading>

                    <div className="table-responsive bg-white rounded-lg mt-4 shadow-md">
                        <table className="table table-striped mt-2" data-testid="email-lists-table">
                            <thead>
                                <tr>
                                    <th>{t('EmailLists.Table.Header.Description')}</th>
                                    <th>{t('EmailLists.Table.Header.ImportedDate')}</th>
                                    <th>{t('EmailLists.Table.Header.RecipientCount')}</th>
                                    <th>{t('EmailLists.Table.Header.SendCount')}</th>
                                    <th>{t('EmailLists.Table.Header.LastSendAt')}</th>
                                    <th>{t('EmailLists.Table.Header.Status')}</th>
                                    <th className="w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {emailLists.map((list) => (
                                    <tr key={list.id}>
                                        <td>{list.description}</td>
                                        <td>{formatDate(list.importedDate)}</td>
                                        <td>{list.recipientCount}</td>
                                        <td>{list.sendCount}</td>
                                        <td>{formatDate(list.lastSendAt)}</td>
                                        <td>{t(`EmailLists.Status.${EmailListStatus[list.status]}`)}</td>
                                        <td>
                                            {webinarId ? (
                                                <Link
                                                    data-testid={`email-list-detail-link-${list.id}`}
                                                    to="/$webinarId/marketing/$id"
                                                    params={{ webinarId, id: list.id }}
                                                    variant="link"
                                                >
                                                    {t('Generic.Button.Details')}
                                                </Link>
                                            ) : (
                                                <Link
                                                    data-testid={`email-list-detail-link-${list.id}`}
                                                    to="/manage/email-lists/$id"
                                                    params={{ id: list.id }}
                                                    variant="link"
                                                >
                                                    {t('Generic.Button.Details')}
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </ContainerDefault>
    );
};
